import { useState, useEffect } from "react";
import { loadSessions, saveSessions } from "./storage";
import { logout } from "./auth";
import type { SessionRecord } from "./types";
import { downloadSessionPdf } from "./pdfReport";

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@700;800;900&family=Barlow:wght@300;400;500;600&display=swap');

  * { margin: 0; padding: 0; box-sizing: border-box; }

  :root {
    --bg: #060b18; --bg2: #0a1020; --cyan: #00c8ff; --purple: #8b5cf6;
    --blue: #2563eb; --blue-h: #1d4ed8; --white: #f0f4ff;
    --muted: #4a5568; --border: rgba(255,255,255,0.07);
    --grad: linear-gradient(90deg, #00c8ff, #8b5cf6);
  }

  body { background: var(--bg); color: var(--white); font-family: 'Barlow', sans-serif; }

  .profile-wrap { min-height: 100vh; background: var(--bg); overflow-x: hidden; }

  .particles { position: fixed; inset: 0; pointer-events: none; z-index: 0; overflow: hidden; }
  .particle {
    position: absolute; width: 2px; height: 2px;
    background: var(--cyan); border-radius: 50%; opacity: 0;
    animation: float linear infinite;
  }
  @keyframes float {
    0%   { opacity:0; transform:translateY(100vh) scale(0); }
    10%  { opacity:0.4; }
    90%  { opacity:0.2; }
    100% { opacity:0; transform:translateY(-10vh) scale(1.5); }
  }

  .topbar {
    position: sticky; top: 0; z-index: 100;
    display: flex; align-items: center; justify-content: space-between;
    padding: 14px 28px;
    background: rgba(6,11,24,0.92); backdrop-filter: blur(12px);
    border-bottom: 1px solid var(--border);
  }
  .logo {
    font-family: 'Barlow Condensed', sans-serif; font-weight: 900;
    font-size: 20px; letter-spacing: 2px;
    background: var(--grad); -webkit-background-clip: text; -webkit-text-fill-color: transparent;
  }
  .logo span { font-weight:300; font-size:11px; color:var(--muted); -webkit-text-fill-color:var(--muted); display:block; letter-spacing:3px; }
  .back-btn {
    background: transparent; border: 1px solid var(--border);
    color: var(--white); padding: 8px 18px; border-radius: 6px;
    font-family: 'Barlow', sans-serif; font-size: 13px; cursor: pointer; transition: all 0.2s;
  }
  .back-btn:hover { border-color: var(--cyan); color: var(--cyan); }

  .content { position: relative; z-index: 1; max-width: 900px; margin: 0 auto; padding: 36px 24px 60px; }

  .section-title {
    font-family: 'Barlow Condensed', sans-serif; font-weight: 800;
    font-size: 13px; letter-spacing: 3px; color: var(--muted);
    text-transform: uppercase; margin-bottom: 14px;
  }

  /* Hero card */
  .profile-hero {
    background: var(--bg2); border: 1px solid var(--border);
    border-radius: 16px; padding: 32px; margin-bottom: 24px;
    display: flex; align-items: center; gap: 28px;
    position: relative; overflow: hidden;
  }
  .profile-hero::before {
    content: '';
    position: absolute; top: -60px; right: -60px;
    width: 200px; height: 200px; border-radius: 50%;
    background: radial-gradient(circle, rgba(0,200,255,0.08) 0%, transparent 70%);
  }
  .avatar-large {
    width: 80px; height: 80px; border-radius: 50%;
    background: linear-gradient(135deg, #00c8ff22, #8b5cf622);
    border: 2px solid rgba(0,200,255,0.4);
    display: flex; align-items: center; justify-content: center;
    font-family: 'Barlow Condensed', sans-serif; font-weight: 900;
    font-size: 28px; color: var(--cyan); flex-shrink: 0;
  }
  .profile-info { flex: 1; }
  .profile-name {
    font-family: 'Barlow Condensed', sans-serif; font-weight: 900;
    font-size: 32px; letter-spacing: 1px;
    background: var(--grad); -webkit-background-clip: text; -webkit-text-fill-color: transparent;
    margin-bottom: 4px;
  }
  .profile-meta { font-size: 13px; color: var(--muted); line-height: 1.8; }
  .profile-badge {
    background: rgba(0,200,255,0.1); border: 1px solid rgba(0,200,255,0.2);
    color: var(--cyan); font-size: 11px; font-weight: 600;
    padding: 3px 10px; border-radius: 20px; display: inline-block; margin-top: 6px;
    letter-spacing: 1px; text-transform: uppercase;
  }

  /* All-time bests */
  .bests-grid {
    display: grid; grid-template-columns: repeat(4,1fr);
    gap: 14px; margin-bottom: 24px;
  }
  @media(max-width:700px){ .bests-grid{grid-template-columns:repeat(2,1fr);} }
  .best-card {
    background: var(--bg2); border: 1px solid var(--border);
    border-radius: 12px; padding: 18px; text-align: center;
    transition: border-color 0.2s;
  }
  .best-card:hover { border-color: rgba(0,200,255,0.2); }
  .best-icon { font-size: 22px; margin-bottom: 8px; }
  .best-val {
    font-family: 'Barlow Condensed', sans-serif; font-weight: 800; font-size: 30px;
    background: var(--grad); -webkit-background-clip: text; -webkit-text-fill-color: transparent;
    margin-bottom: 2px;
  }
  .best-label { font-size: 10px; color: var(--muted); letter-spacing: 1px; text-transform: uppercase; }
  .best-date { font-size: 10px; color: rgba(0,200,255,0.4); margin-top: 3px; }

  /* Sessions list */
  .sessions-list { display: flex; flex-direction: column; gap: 12px; margin-bottom: 24px; }
  .session-row {
    background: var(--bg2); border: 1px solid var(--border);
    border-radius: 10px; padding: 16px 20px;
    display: flex; align-items: center; justify-content: space-between;
    transition: border-color 0.2s;
  }
  .session-row:hover { border-color: rgba(255,255,255,0.12); }
  .session-row-left { display: flex; align-items: center; gap: 14px; }
  .session-num {
    font-family: 'Barlow Condensed', sans-serif; font-weight: 800;
    font-size: 20px; color: var(--cyan); width: 32px;
  }
  .session-row-info {}
  .session-row-name { font-weight: 600; font-size: 14px; margin-bottom: 2px; }
  .session-row-meta { font-size: 11px; color: var(--muted); }
  .session-row-right { display: flex; align-items: center; gap: 14px; }
  .session-score-ring { /* mini ring */ }
  .session-scores-mini { text-align: right; }
  .session-score-big {
    font-family: 'Barlow Condensed', sans-serif; font-weight: 800; font-size: 28px;
    color: var(--cyan);
  }
  .session-score-sub { font-size: 10px; color: var(--muted); }

  /* Settings */
  .settings-card {
    background: var(--bg2); border: 1px solid var(--border);
    border-radius: 12px; overflow: hidden; margin-bottom: 24px;
  }
  .setting-row {
    display: flex; align-items: center; justify-content: space-between;
    padding: 16px 20px; border-bottom: 1px solid var(--border);
    transition: background 0.15s;
  }
  .setting-row:last-child { border-bottom: none; }
  .setting-row:hover { background: rgba(255,255,255,0.02); }
  .setting-label { font-size: 14px; font-weight: 500; }
  .setting-sub { font-size: 11px; color: var(--muted); margin-top: 2px; }
  .setting-action {
    background: transparent; border: 1px solid var(--border);
    color: var(--muted); padding: 6px 14px; border-radius: 6px;
    font-family: 'Barlow', sans-serif; font-size: 12px; cursor: pointer;
    transition: all 0.2s; white-space: nowrap;
  }
  .setting-action:hover { border-color: rgba(255,255,255,0.2); color: var(--white); }
  .setting-action.danger:hover { border-color: #ef4444; color: #ef4444; }
  .setting-action.connected { border-color: rgba(34,197,94,0.4); color: #22c55e; }

  /* Confirm dialog */
  .confirm-overlay {
    position: fixed; inset: 0; background: rgba(0,0,0,0.7);
    display: flex; align-items: center; justify-content: center;
    z-index: 200; backdrop-filter: blur(4px);
  }
  .confirm-box {
    background: var(--bg2); border: 1px solid rgba(239,68,68,0.3);
    border-radius: 14px; padding: 28px 32px; max-width: 380px; text-align: center;
  }
  .confirm-title { font-family: 'Barlow Condensed', sans-serif; font-weight: 800; font-size: 22px; margin-bottom: 10px; }
  .confirm-desc { font-size: 13px; color: var(--muted); margin-bottom: 24px; line-height: 1.6; }
  .confirm-btns { display: flex; gap: 10px; justify-content: center; }
  .btn-danger { background: #ef4444; color: #fff; border: none; padding: 10px 24px; border-radius: 8px; font-family: 'Barlow', sans-serif; font-weight: 600; font-size: 13px; cursor: pointer; transition: background 0.2s; }
  .btn-danger:hover { background: #dc2626; }
  .btn-cancel { background: transparent; border: 1px solid var(--border); color: var(--white); padding: 10px 24px; border-radius: 8px; font-family: 'Barlow', sans-serif; font-size: 13px; cursor: pointer; transition: all 0.2s; }
  .btn-cancel:hover { border-color: rgba(255,255,255,0.2); }

  @keyframes fadeUp { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
  .fade-up { animation: fadeUp 0.4s ease both; }
  .d1{animation-delay:0.05s} .d2{animation-delay:0.1s} .d3{animation-delay:0.15s} .d4{animation-delay:0.2s}
`;

interface Props { onBack: () => void; onLogout: () => void; playerName?: string; }

export default function ProfilePage({ onBack, onLogout, playerName }: Props) {
  const [showConfirm, setShowConfirm] = useState(false);
  const [sessions, setSessions] = useState<SessionRecord[]>([]);

  useEffect(() => { setSessions(loadSessions()); }, []);

  function handleClear() {
    saveSessions([]);
    setSessions([]);
    setShowConfirm(false);
  }

  function handleExport() {
    const blob = new Blob([JSON.stringify(sessions, null, 2)], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "shuttlecock_sessions.json";
    a.click();
  }

  function handleDownloadPdf(s: SessionRecord) {
    downloadSessionPdf({
      playerName: playerName || "Player",
      playerEmail: "",
      session: s,
      tips: [],
    });
  }

  const name = playerName || "Athlete";
  const ini  = name.split(" ").map((w: string) => w[0]).join("").slice(0, 2).toUpperCase();
  const hasSessions = sessions.length > 0;

  const bests = hasSessions ? [
    { icon: "🏆", val: `${Math.max(...sessions.map(s=>s.smash||0))}`,      label: "Best Smash Score",  date: sessions.find(s=>s.smash===Math.max(...sessions.map(x=>x.smash||0)))?.date || "" },
    { icon: "🦘", val: `${Math.max(...sessions.map(s=>s.jumpHeight||0))} cm`, label: "Jump Height",    date: sessions.find(s=>s.jumpHeight===Math.max(...sessions.map(x=>x.jumpHeight||0)))?.date || "" },
    { icon: "🎯", val: `${Math.max(...sessions.map(s=>s.overall||0))}`,    label: "Best Overall",      date: sessions.find(s=>s.overall===Math.max(...sessions.map(x=>x.overall||0)))?.date || "" },
    { icon: "🏸", val: `${Math.max(...sessions.map(s=>s.shots||0))}`,      label: "Most Shots",        date: sessions.find(s=>s.shots===Math.max(...sessions.map(x=>x.shots||0)))?.date || "" },
  ] : [];

  return (
    <>
      <style>{css}</style>
      <div className="profile-wrap">
        <div className="particles">
          {Array.from({ length: 14 }).map((_, i) => (
            <div key={i} className="particle" style={{
              left: `${Math.random() * 100}%`,
              animationDuration: `${8 + Math.random() * 12}s`,
              animationDelay: `${Math.random() * 8}s`,
            }} />
          ))}
        </div>

        <div className="topbar">
          <div className="logo">SHUTTLECOCK <span>BY STATVO</span></div>
          <div style={{ fontSize: 13, color: "var(--muted)", fontFamily: "Barlow Condensed", letterSpacing: 2 }}>PROFILE</div>
          <button className="back-btn" onClick={onBack}>← Dashboard</button>
        </div>

        <div className="content">

          {/* Hero */}
          <div className="profile-hero fade-up">
            <div className="avatar-large">{ini}</div>
            <div className="profile-info">
              <div className="profile-name">{name}</div>
              <div className="profile-meta">
                {sessions.length} session{sessions.length !== 1 ? "s" : ""} recorded
              </div>
              <div className="profile-badge">Badminton Athlete</div>
            </div>
          </div>

          {/* All-time bests */}
          <div className="section-title fade-up d1">All-Time Bests</div>
          {hasSessions && <div className="bests-grid fade-up d1">
            {bests.map((b, i) => (
              <div className="best-card" key={i}>
                <div className="best-icon">{b.icon}</div>
                <div className="best-val">{b.val}</div>
                <div className="best-label">{b.label}</div>
                <div className="best-date">{b.date}</div>
              </div>
            ))}
          </div>}

          {/* Sessions history */}
          <div className="section-title fade-up d2">Session History</div>
          <div className="sessions-list fade-up d2">
            {hasSessions ? sessions.map((s, i) => (
              <div className="session-row" key={s.id}>
                <div className="session-row-left">
                  <div className="session-num">#{sessions.length - i}</div>
                  <div className="session-row-info">
                    <div className="session-row-name">{s.label}</div>
                    <div className="session-row-meta">{s.date} · {s.duration} · {s.shots} shots</div>
                  </div>
                </div>
                <div className="session-row-right" style={{ display:"flex", alignItems:"center", gap:12 }}>
                  <div className="session-scores-mini">
                    <div className="session-score-big">{s.overall}</div>
                    <div className="session-score-sub">Overall · Smash {s.smash}</div>
                  </div>
                  <button onClick={() => handleDownloadPdf(s)}
                    style={{ background:"rgba(0,200,255,0.1)", border:"1px solid rgba(0,200,255,0.3)",
                      borderRadius:6, color:"#00c8ff", fontSize:11, fontWeight:600,
                      padding:"6px 10px", cursor:"pointer", fontFamily:"Barlow, sans-serif", whiteSpace:"nowrap" as const }}>
                    ↓ PDF
                  </button>
                </div>
              </div>
            )) : (
              <div style={{ padding: "24px", textAlign: "center", color: "var(--muted)", fontSize: 14 }}>
                No sessions yet. Upload a video to get started.
              </div>
            )}
          </div>

          {/* Settings */}
          <div className="section-title fade-up d3">Settings & Data</div>
          <div className="settings-card fade-up d3">
            <div className="setting-row">
              <div>
                <div className="setting-label">Local Storage</div>
                <div className="setting-sub">Sessions saved to your browser's localStorage</div>
              </div>
              <button className="setting-action connected">✓ Active</button>
            </div>

            <div className="setting-row">
              <div>
                <div className="setting-label">Clear Session Data</div>
                <div className="setting-sub">Permanently delete all recorded sessions</div>
              </div>
              <button className="setting-action danger" onClick={() => setShowConfirm(true)}
                disabled={!hasSessions} style={{ opacity: hasSessions ? 1 : 0.4 }}>Clear Data</button>
            </div>
            <div className="setting-row">
              <div>
                <div className="setting-label">Sign Out</div>
                <div className="setting-sub">Return to login screen</div>
              </div>
              <button className="setting-action danger" onClick={async () => { await logout(); onLogout(); }}>Sign Out</button>
            </div>
          </div>


        </div>

        {/* Confirm dialog */}
        {showConfirm && (
          <div className="confirm-overlay">
            <div className="confirm-box">
              <div className="confirm-title">Clear All Data?</div>
              <div className="confirm-desc">
                This will permanently delete all session data from Google Drive. This cannot be undone.
              </div>
              <div className="confirm-btns">
                <button className="btn-cancel" onClick={() => setShowConfirm(false)}>Cancel</button>
                <button className="btn-danger" onClick={handleClear}>Yes, Clear</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}